package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element

/** Draw every data mark with one treatment so the static, preview and reveal layers cannot drift. */
@Composable
internal fun LineChartMarks(
    series: List<ChartSeries>,
    colors: List<Color>,
    ring: String,
    steps: Int,
    area: Boolean,
    x: (Int) -> Double,
    y: (Double) -> Double,
    areaOpacity: Double = ChartPalette.AREA_OPACITY,
) {
    series.forEachIndexed { seriesIndex, one ->
        val color = colors[seriesIndex % colors.size]
        val line = one.points.mapIndexed { index, point ->
            "${if (index == 0) "M" else "L"}${x(index).round()} ${y(point.value).round()}"
        }.joinToString(" ")
        if (area) {
            SvgTag("path", {
                attr(
                    "d",
                    "$line L${x(steps - 1).round()} ${y(0.0).round()} " +
                        "L${x(0).round()} ${y(0.0).round()} Z",
                )
                attr("fill", color.toString())
                attr("fill-opacity", areaOpacity.toString())
            })
        }
        SvgTag("path", {
            attr("d", line)
            attr("fill", "none")
            attr("stroke", color.toString())
            attr("stroke-opacity", "1.0")
            attr("stroke-width", ChartPalette.LINE_WIDTH.toString())
            attr("stroke-linecap", "round")
            attr("stroke-linejoin", "round")
            ChartPalette.dash(seriesIndex)?.let { attr("stroke-dasharray", it) }
        })
        ChartMarker(
            cx = x(steps - 1),
            cy = y(one.points.last().value),
            shape = ChartPalette.marker(seriesIndex),
            fill = color.toString(),
            ring = ring,
        )
    }
}

/** One step is a reading, not a line. Say the number rather than draw a dot on an axis. */
@Composable
internal fun SingleChartPoint(series: List<ChartSeries>, valueFormat: (Double) -> String) {
    val colorMode = ColorMode.current
    val colors = ChartPalette.categorical(colorMode)
    ChartReadout(
        label = series.first().points.first().label,
        entries = series.mapIndexed { index, one ->
            Triple(one.name, valueFormat(one.points.first().value), colors[index % colors.size])
        },
    )
}

@Composable
internal fun ChartLegend(names: List<String>, colors: List<Color>) {
    val palette = ColorMode.current.toSitePalette()
    Row(Modifier.gap(Space.MD).styleModifier { property("flex-wrap", "wrap") }) {
        names.forEachIndexed { index, name ->
            Row(Modifier.gap(Space.XXS), verticalAlignment = Alignment.CenterVertically) {
                Svg({
                    attr("width", "26"); attr("height", "12")
                    attr("viewBox", "0 0 26 12")
                    attr("aria-hidden", "true")
                    attr("focusable", "false")
                }) {
                    val color = colors[index % colors.size].toString()
                    SvgTag("path", {
                        attr("d", "M1 6 H25")
                        attr("stroke", color)
                        attr("stroke-width", ChartPalette.LINE_WIDTH.toString())
                        attr("stroke-linecap", "round")
                        ChartPalette.dash(index)?.let { attr("stroke-dasharray", it) }
                    })
                    ChartMarker(
                        cx = 13.0,
                        cy = 6.0,
                        shape = ChartPalette.marker(index),
                        fill = color,
                        ring = palette.surface.toString(),
                        r = 3.5,
                    )
                }
                Span(Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs()) {
                    Text(name)
                }
            }
        }
    }
}

@Composable
internal fun ChartReadout(label: String, entries: List<Triple<String, String, Color>>) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier
            .fillMaxWidth()
            .gap(Space.MD)
            .styleModifier { property("flex-wrap", "wrap") }
            .attr("data-chart-readout", "")
            .attr("aria-live", "polite")
            .attr("aria-atomic", "true"),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Span(Modifier.fontSize(TypeScale.Small).fontWeight(600).toAttrs()) { Text(label) }
        entries.forEach { (name, value, color) ->
            Row(Modifier.gap(Space.XXS), verticalAlignment = Alignment.CenterVertically) {
                Div(
                    Modifier.styleModifier {
                        property("width", "8px"); property("height", "8px")
                        property("border-radius", "999px")
                        property("background", color.toString())
                    }.toAttrs(),
                ) {}
                Span(Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs()) {
                    Text(tf("ds.ds.lline_chart_parts.series_name", "{name} ", "name" to name))
                }
                Span(
                    Modifier
                        .fontSize(TypeScale.Small)
                        .fontWeight(600)
                        .styleModifier { property("font-variant-numeric", "tabular-nums") }
                        .toAttrs(),
                ) {
                    Text(value)
                }
            }
        }
    }
}

/** A shape marker keeps series distinguishable without relying on colour. */
@Composable
internal fun ChartMarker(
    cx: Double,
    cy: Double,
    shape: MarkerShape,
    fill: String,
    ring: String,
    r: Double = ChartPalette.MARKER_RADIUS,
) {
    val common: AttrsScope<Element>.() -> Unit = {
        attr("fill", fill)
        attr("stroke", ring)
        attr("stroke-width", "2")
        attr("stroke-linejoin", "round")
    }
    when (shape) {
        MarkerShape.CIRCLE -> SvgTag("circle", {
            attr("cx", cx.round()); attr("cy", cy.round()); attr("r", r.toString()); common()
        })
        MarkerShape.SQUARE -> SvgTag("rect", {
            attr("x", (cx - r).round()); attr("y", (cy - r).round())
            attr("width", (r * 2).round()); attr("height", (r * 2).round()); common()
        })
        MarkerShape.TRIANGLE -> SvgTag("polygon", {
            val h = r * 1.15
            attr("points", "${cx.round()},${(cy - h).round()} " +
                "${(cx + h).round()},${(cy + h * 0.8).round()} " +
                "${(cx - h).round()},${(cy + h * 0.8).round()}")
            common()
        })
        MarkerShape.DIAMOND -> SvgTag("polygon", {
            val d = r * 1.25
            attr("points", "${cx.round()},${(cy - d).round()} ${(cx + d).round()},${cy.round()} " +
                "${cx.round()},${(cy + d).round()} ${(cx - d).round()},${cy.round()}")
            common()
        })
        MarkerShape.PLUS -> SvgTag("path", {
            val a = r * 1.3
            attr("d", "M${(cx - a).round()} ${cy.round()} H${(cx + a).round()} " +
                "M${cx.round()} ${(cy - a).round()} V${(cy + a).round()}")
            attr("stroke", fill)
            attr("stroke-width", "2.5")
            attr("stroke-linecap", "round")
            attr("fill", "none")
        })
    }
}
